import { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  useV1Customers,
  useInviteMigration,
  type V1ListOpts,
} from "@/api/migration";

const PAGE_SIZE = 25;

/**
 * Dialog for v1→v2 customer migration. Lists v1 customers (paginated),
 * lets admin select rows, fires /api/v2/migration/invite. Status badge
 * per row shows whether the customer was already migrated.
 *
 * Phase 4 of the v2 cutover plan; remove after Phase 7 retirement.
 */
export function MigrateFromV1Dialog() {
  const [open, setOpen] = useState(false);
  const [status, setStatus] =
    useState<NonNullable<V1ListOpts["status"]>>("ACTIVE");
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const { data, isLoading, isError, error } = useV1Customers({
    status,
    limit: PAGE_SIZE,
    offset,
  });
  const invite = useInviteMigration();

  // Selectable = visible rows that aren't already migrated and have an email.
  const selectableEmails = useMemo(() => {
    return (data?.items ?? [])
      .filter((c) => !c.migratedToV2 && c.email)
      .map((c) => c.email!);
  }, [data]);

  const allSelected =
    selectableEmails.length > 0 &&
    selectableEmails.every((e) => selected.has(e));

  function toggleAll() {
    if (allSelected) {
      const next = new Set(selected);
      selectableEmails.forEach((e) => next.delete(e));
      setSelected(next);
    } else {
      const next = new Set(selected);
      selectableEmails.forEach((e) => next.add(e));
      setSelected(next);
    }
  }

  function toggleOne(email: string) {
    const next = new Set(selected);
    if (next.has(email)) next.delete(email);
    else next.add(email);
    setSelected(next);
  }

  async function handleInvite() {
    if (selected.size === 0) return;
    try {
      const result = await invite.mutateAsync(Array.from(selected));
      const invitedCount = result.invited.length;
      const skippedCount = result.skipped.length;
      const skippedSummary = summarizeSkipped(result.skipped);
      toast.success(`Invited ${invitedCount}`, {
        description:
          skippedCount > 0
            ? `${skippedCount} skipped: ${skippedSummary}`
            : "All selected customers received invites.",
      });
      setSelected(new Set());
    } catch (err) {
      toast.error("Invite failed", {
        description: err instanceof Error ? err.message : "Unknown error",
      });
    }
  }

  function handleOpenChange(next: boolean) {
    setOpen(next);
    if (!next) {
      // reset state when closed so re-open is clean
      setSelected(new Set());
      setOffset(0);
    }
  }

  const total = data?.total ?? 0;
  const items = data?.items ?? [];
  const pageStart = total === 0 ? 0 : offset + 1;
  const pageEnd = Math.min(offset + PAGE_SIZE, total);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline">Migrate from v1</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[900px] max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Migrate customers from v1</DialogTitle>
          <DialogDescription>
            Select v1 customers to send activation emails. Each customer gets a
            magic link to set their v2 password and finish migrating.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between gap-4 py-2">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Status:</span>
            <Select
              value={status}
              onValueChange={(v) => {
                setStatus(v as V1ListOpts["status"] & string);
                setOffset(0);
              }}
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ACTIVE">Active</SelectItem>
                <SelectItem value="INACTIVE">Inactive</SelectItem>
                <SelectItem value="DELETED">Deleted</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="text-sm text-muted-foreground">
            {selected.size > 0
              ? `${selected.size} selected`
              : `${total} total v1 customers`}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto border rounded-md">
          <Table>
            <TableHeader className="sticky top-0 bg-background">
              <TableRow>
                <TableHead className="w-[40px]">
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={toggleAll}
                    disabled={selectableEmails.length === 0}
                    aria-label="Select all on page"
                  />
                </TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Name</TableHead>
                <TableHead className="w-[100px]">Number</TableHead>
                <TableHead className="w-[140px]">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    Loading…
                  </TableCell>
                </TableRow>
              )}
              {isError && (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="text-center py-8 text-destructive"
                  >
                    Failed to load:{" "}
                    {error instanceof Error ? error.message : "unknown"}
                  </TableCell>
                </TableRow>
              )}
              {!isLoading && items.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="text-center py-8 text-muted-foreground"
                  >
                    No v1 customers in this status.
                  </TableCell>
                </TableRow>
              )}
              {items.map((c) => {
                const email = c.email ?? "";
                const disabled = c.migratedToV2 || !email;
                const checked = email ? selected.has(email) : false;
                return (
                  <TableRow key={c.id}>
                    <TableCell>
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => email && toggleOne(email)}
                        disabled={disabled}
                        aria-label={`Select ${email}`}
                      />
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {email || (
                        <span className="text-muted-foreground italic">
                          no email
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {[c.firstName, c.lastName].filter(Boolean).join(" ") || (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      #{c.customerNumber}
                    </TableCell>
                    <TableCell>
                      {c.migratedToV2 ? (
                        <Badge className="bg-green-600">Migrated</Badge>
                      ) : (
                        <Badge variant="outline">Not migrated</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        <DialogFooter className="flex items-center justify-between gap-2 sm:justify-between">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>
              {pageStart}–{pageEnd} of {total}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              disabled={offset === 0}
            >
              Prev
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOffset(offset + PAGE_SIZE)}
              disabled={offset + PAGE_SIZE >= total}
            >
              Next
            </Button>
          </div>
          <Button
            onClick={handleInvite}
            disabled={selected.size === 0 || invite.isPending}
          >
            {invite.isPending
              ? "Inviting…"
              : `Invite ${selected.size || ""}`.trim()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function summarizeSkipped(
  skipped: { email: string; reason: string }[]
): string {
  const counts: Record<string, number> = {};
  for (const s of skipped) {
    counts[s.reason] = (counts[s.reason] ?? 0) + 1;
  }
  return Object.entries(counts)
    .map(([reason, n]) => `${n} ${reason.replace(/_/g, " ")}`)
    .join(", ");
}
